import { useCallback } from 'react'

import { toast } from 'react-toastify'

import { DefaultDropCharge } from 'constants/EntityConstants/Booking'
import { BookingChargeDefaultsByType } from 'constants/EntityConstants/OtherChargeEntity'
import { BookingChargeType } from 'generated/graphql'
import { getUserStops } from 'helpers/waitAndReturn.helpers'
import { TBookingAddressForm, TBookingChargeForm } from 'types/form-types'

type IUseDropChargeSyncProps = {
  tariffDetails: any
  collectionAddresses: TBookingAddressForm[]
  deliveryAddresses: TBookingAddressForm[]
}

export const useDropChargeSync = ({
  tariffDetails,
  collectionAddresses,
  deliveryAddresses,
}: IUseDropChargeSyncProps) => {
  const handleDropChargeRemoved = useCallback((charges: TBookingChargeForm[]) => {
    const currentDropCharge = charges.find((x) => x?.type === BookingChargeType.Drop)

    if (!currentDropCharge) {
      return
    }

    charges.splice(0, charges.length, ...charges.filter((x) => x?.type !== BookingChargeType.Drop))
    toast.info('Automatic charge Drop was removed.')
  }, [])

  const handleDropChargeApplied = useCallback(
    (charges: TBookingChargeForm[], dropQuantity: number) => {
      if (!tariffDetails?.isDropChargeEnabled) {
        return
      }

      const rate = tariffDetails.isUseDefaultDropCharge
        ? DefaultDropCharge
        : tariffDetails.dropChargeRate ?? 0

      const currentDropChargeIndex = charges.findIndex((x) => x?.type === BookingChargeType.Drop)

      if (currentDropChargeIndex === -1) {
        const dropChargeDefaults = BookingChargeDefaultsByType[BookingChargeType.Drop]
        if (!dropChargeDefaults) {
          return
        }

        const newDropCharge: TBookingChargeForm = {
          ...dropChargeDefaults,
          quantity: dropQuantity,
          rate,
        }

        charges.push(newDropCharge)

        toast.info('Automatic charge Drop was added.')
        return
      }

      const currentDropCharge = charges[currentDropChargeIndex]
      if (currentDropCharge.quantity === dropQuantity && currentDropCharge.rate === rate) {
        return
      }

      const updatedDropCharge: TBookingChargeForm = {
        ...currentDropCharge,
        quantity: dropQuantity,
        rate,
      }

      charges[currentDropChargeIndex] = updatedDropCharge

      toast.info('Automatic charge Drop was updated.')
    },
    [
      tariffDetails?.dropChargeRate,
      tariffDetails?.isDropChargeEnabled,
      tariffDetails?.isUseDefaultDropCharge,
    ],
  )

  const handleDropChargeSync = useCallback(
    (charges: TBookingChargeForm[]) => {
      const hastWaitAndReturnCharge = charges.some(
        (bc) => bc?.type === BookingChargeType.WaitAndReturn,
      )
      if (hastWaitAndReturnCharge) {
        return
      }

      // Generated Wait & Return stops are not drops.
      const dropQuantity =
        getUserStops(collectionAddresses).length + getUserStops(deliveryAddresses).length - 2

      if (dropQuantity <= 0) {
        handleDropChargeRemoved(charges)
        return
      }

      handleDropChargeApplied(charges, dropQuantity)
    },
    [collectionAddresses, deliveryAddresses, handleDropChargeApplied, handleDropChargeRemoved],
  )

  return { handleDropChargeSync }
}
